// Every <select> in the game opens this themed list instead of the browser's own (a plain white pop-out on
// Windows and Android). It works on any select, wherever and however it is rendered: one listener at the
// document. Picking an option sets the select's value and fires input and change, so callers see a normal
// select. Keyboard: Enter, Space or Alt+Down opens it; arrows, Home/End, Enter and Escape work in the list.

let open: { sel: HTMLSelectElement; pop: HTMLElement; items: HTMLElement[]; at: number } | null = null;
let pressed: HTMLSelectElement | null = null;

const usable = (t: EventTarget | null): HTMLSelectElement | null => {
  const s = t instanceof Element ? t.closest('select') : null;
  return s && !s.disabled && !s.multiple && (s.size ?? 0) <= 1 && !s.hasAttribute('data-native') ? s : null;
};

function close(refocus = false) {
  if (!open) return;
  const { sel, pop } = open;
  open = null;
  pop.remove();
  sel.removeAttribute('data-open');
  sel.setAttribute('aria-expanded', 'false');
  if (refocus) sel.focus({ preventScroll: true });
}

function choose(i: number) {
  if (!open) return;
  const { sel, items } = open;
  const v = items[i]?.dataset.v;
  close(true);
  if (v === undefined || v === sel.value) return;
  sel.value = v;
  sel.dispatchEvent(new Event('input', { bubbles: true }));
  sel.dispatchEvent(new Event('change', { bubbles: true }));
}

function mark(i: number, scroll = true) {
  if (!open) return;
  const n = open.items.length;
  if (!n) return;
  // Skip disabled options in the direction of travel.
  const dir = i < open.at ? -1 : 1;
  let j = Math.max(0, Math.min(n - 1, i));
  while (open.items[j]?.getAttribute('aria-disabled') === 'true' && j + dir >= 0 && j + dir < n) j += dir;
  open.items[open.at]?.classList.remove('hi');
  open.at = j;
  open.items[j].classList.add('hi');
  if (scroll) open.items[j].scrollIntoView({ block: 'nearest' });
}

function place(sel: HTMLSelectElement, pop: HTMLElement) {
  const r = sel.getBoundingClientRect(), vw = innerWidth, vh = innerHeight, gap = 4, pad = 8;
  pop.style.minWidth = `${Math.round(r.width)}px`;
  pop.style.maxWidth = `${vw - pad * 2}px`;
  const below = vh - r.bottom - gap - pad, above = r.top - gap - pad;
  const want = pop.scrollHeight;
  const up = want > below && above > below;
  const room = Math.max(120, up ? above : below);
  pop.style.maxHeight = `${Math.round(Math.min(room, vh - pad * 2))}px`;
  const h = Math.min(want, room);
  pop.style.top = `${Math.round(up ? Math.max(pad, r.top - gap - h) : Math.min(r.bottom + gap, vh - pad - h))}px`;
  const w = pop.offsetWidth;
  pop.style.left = `${Math.round(Math.max(pad, Math.min(r.left, vw - pad - w)))}px`;
}

function show(sel: HTMLSelectElement) {
  close();
  const pop = document.createElement('div');
  pop.className = 'selpop';
  pop.setAttribute('role', 'listbox');
  const label = sel.getAttribute('aria-label') || sel.closest('label')?.textContent?.trim();
  if (label) pop.setAttribute('aria-label', label);
  const items: HTMLElement[] = [];
  let at = 0;
  for (const node of Array.from(sel.children)) {
    const opts = node instanceof HTMLOptGroupElement ? Array.from(node.children) : [node];
    if (node instanceof HTMLOptGroupElement) {
      const g = document.createElement('div');
      g.className = 'selpop-grp';
      g.textContent = node.label;
      pop.append(g);
    }
    for (const o of opts) {
      if (!(o instanceof HTMLOptionElement) || o.hidden) continue;
      const it = document.createElement('div');
      it.className = 'selpop-opt';
      it.setAttribute('role', 'option');
      it.dataset.v = o.value;
      it.dataset.i = String(items.length);
      it.textContent = o.textContent;
      if (o.disabled || (node instanceof HTMLOptGroupElement && node.disabled)) it.setAttribute('aria-disabled', 'true');
      if (o.selected) { it.setAttribute('aria-selected', 'true'); at = items.length; }
      items.push(it);
      pop.append(it);
    }
  }
  if (!items.length) return;
  // A select inside a dialog keeps its font size; the list matches it.
  const cs = getComputedStyle(sel);
  pop.style.fontSize = cs.fontSize;
  document.body.append(pop);
  open = { sel, pop, items, at };
  sel.setAttribute('data-open', '');
  sel.setAttribute('aria-expanded', 'true');
  place(sel, pop);
  mark(at);
  pop.addEventListener('pointermove', (e) => {
    const it = (e.target as Element).closest<HTMLElement>('.selpop-opt');
    if (it && it.getAttribute('aria-disabled') !== 'true' && open) mark(Number(it.dataset.i), false);
  });
  pop.addEventListener('click', (e) => {
    const it = (e.target as Element).closest<HTMLElement>('.selpop-opt');
    if (it && it.getAttribute('aria-disabled') !== 'true') choose(Number(it.dataset.i));
  });
}

export function initSelectPop() {
  const block = (e: Event) => { if (usable(e.target)) e.preventDefault(); };
  // The native list opens on press (mouse) or tap (touch): stop both, open ours on release.
  document.addEventListener('pointerdown', (e) => {
    const s = usable(e.target);
    if (open && !(e.target instanceof Node && open.pop.contains(e.target))) {
      const same = s === open.sel;
      close();
      if (same) { e.preventDefault(); pressed = null; return; }
    }
    if (!s) return;
    e.preventDefault();
    pressed = s;
  }, true);
  document.addEventListener('mousedown', block, true);
  document.addEventListener('touchstart', block, { capture: true, passive: false });
  document.addEventListener('touchend', block, { capture: true, passive: false });
  document.addEventListener('click', block, true);
  document.addEventListener('pointerup', (e) => {
    const s = usable(e.target);
    if (s && s === pressed) { e.preventDefault(); s.focus({ preventScroll: true }); show(s); }
    pressed = null;
  }, true);
  document.addEventListener('keydown', (e) => {
    if (open) {
      const n = open.items.length;
      const k = e.key;
      if (k === 'Escape' || k === 'Tab') { e.preventDefault(); e.stopImmediatePropagation(); close(true); return; }
      if (k === 'ArrowDown') mark(open.at + 1);
      else if (k === 'ArrowUp') mark(open.at - 1);
      else if (k === 'Home') mark(0);
      else if (k === 'End') mark(n - 1);
      else if (k === 'PageDown') mark(open.at + 8);
      else if (k === 'PageUp') mark(open.at - 8);
      else if (k === 'Enter' || k === ' ') choose(open.at);
      else if (k.length === 1 && /\S/.test(k)) {
        // Type to jump: the next option starting with that letter.
        const c = k.toLowerCase();
        for (let d = 1; d <= n; d++) {
          const i = (open.at + d) % n;
          if (open.items[i].textContent?.trim().toLowerCase().startsWith(c)) { mark(i); break; }
        }
      } else return;
      e.preventDefault(); e.stopImmediatePropagation();
      return;
    }
    const s = usable(e.target);
    if (s && (e.key === 'Enter' || e.key === ' ' || (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) || e.key === 'F4')) {
      e.preventDefault(); e.stopImmediatePropagation(); show(s);
    }
  }, true);
  // Anything that moves the select away closes the list.
  addEventListener('resize', () => close());
  document.addEventListener('scroll', (e) => { if (open && !(e.target instanceof Node && open.pop.contains(e.target))) close(); }, true);
  // A re-render that drops the select closes its list too.
  new MutationObserver(() => { if (open && !open.sel.isConnected) close(); }).observe(document.body, { childList: true, subtree: true });
}
